import React from 'react';
import { CheckCircle, Search, FileCheck, Shield, Award, Clock, Users, TrendingUp } from 'lucide-react';
import ServiceFAQ from '../../components/ServiceFAQ';

const Trademark = ({ onGetStarted }) => {
  const benefits = [
    'Comprehensive trademark searches',
    'Federal and state registration filing',
    'Office action response and prosecution',
    'Trademark portfolio management',
    'Enforcement and infringement defense',
    'Brand protection strategies',
  ];

  const process = [
    { title: 'Trademark Search', description: 'Comprehensive search to ensure availability and registrability.' },
    { title: 'Application Preparation', description: 'Prepare and file trademark application with USPTO.' },
    { title: 'Office Action Response', description: 'Address any USPTO objections or requirements.' },
    { title: 'Registration & Protection', description: 'Secure registration and implement protection strategy.' },
  ];

  const services = [
    'Trademark Clearance Searches',
    'Federal Trademark Registration',
    'State Trademark Registration',
    'International Trademark Filing',
    'Trademark Monitoring',
    'Cease & Desist Letters',
    'Infringement Litigation',
    'Trademark Portfolio Management',
  ];

  const whyChoose = [
    { icon: Award, title: 'Proven Registration Success', description: 'Successfully registered trademarks across diverse industries.' },
    { icon: Shield, title: 'Comprehensive Protection', description: 'Full-spectrum trademark services from search to enforcement.' },
    { icon: Clock, title: 'Efficient Process', description: 'Streamlined filing and prosecution for faster results.' },
    { icon: TrendingUp, title: 'Strategic Guidance', description: 'Brand protection strategies aligned with business goals.' },
  ];

  const faqs = [
    { question: 'How long does trademark registration take?', answer: 'The trademark registration process typically takes 8-12 months from filing to approval. This includes the initial examination period, publication for opposition, and final registration. Timelines can vary based on Office actions and potential oppositions.' },
    { question: 'What can be trademarked?', answer: 'You can trademark brand names, logos, slogans, product names, service names, and even sounds or colors in certain cases. The mark must be distinctive and used in commerce to identify your goods or services.' },
    { question: 'Do I need a trademark attorney?', answer: 'While not legally required, hiring a trademark attorney significantly increases your chances of successful registration. We conduct proper searches, prepare strong applications, and handle any Office actions that arise.' },
    { question: 'How much does trademark registration cost?', answer: 'Costs vary based on the number of classes and complexity. USPTO filing fees start at $250-$350 per class. Our attorney fees depend on the scope of services needed, including searches and application preparation.' },
    { question: 'What is the difference between ® and ™?', answer: 'The ® symbol can only be used after your trademark is officially registered with the USPTO. The ™ symbol can be used as soon as you start using your mark in commerce, even before registration.' },
  ];

  return (
    <div className="pt-20" data-testid="service-trademark">
      <section 
        className="py-24 md:py-32 relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/5673483/pexels-photo-5673483.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-[#2C3E50]/80"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="max-w-3xl">
            <div className="text-sm uppercase tracking-[0.2em] font-medium text-white/90 mb-4">Intellectual Property Services</div>
            <h1 className="text-5xl lg:text-6xl tracking-tight leading-none text-white font-bold mb-6">Trademark Registration</h1>
            <p className="text-xl text-white/90 leading-relaxed mb-8">Protect your brand identity with comprehensive trademark registration services. We handle the entire process from search to registration.</p>
            <button onClick={onGetStarted} className="bg-[#CA9A67] text-white hover:bg-[#b8894f] transition-colors px-8 py-4 font-medium" data-testid="service-cta-button">Get Started</button>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-6">Overview</h2>
              <p className="text-base leading-relaxed text-[#2E2E2E]/80 mb-6">Your trademark is a valuable asset that distinguishes your business from competitors. Our experienced IP attorneys provide end-to-end trademark services, from initial clearance searches through registration and enforcement.</p>
              <p className="text-base leading-relaxed text-[#2E2E2E]/80">We have successfully registered trademarks for clients across diverse industries, protecting their brand identity and market position.</p>
              <h3 className="text-xl lg:text-2xl text-[#2C3E50] font-semibold mb-6 mt-8">Key Benefits</h3>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-[#2C3E50] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    <span className="text-base text-[#2E2E2E]/80">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <img src="https://images.unsplash.com/photo-1583521214690-73421a1829a9?w=800&q=80" alt="Trademark registration resources" className="w-full h-[450px] object-cover shadow-lg" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-4">Our Trademark Services</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mobile-swiper">
            {services.map((service, index) => (
              <div key={index} className="bg-white border border-[#2C3E50]/5 shadow-sm p-6">
                <FileCheck className="w-8 h-8 text-[#CA9A67] mb-3" strokeWidth={1.5} />
                <h4 className="font-semibold text-[#2C3E50]">{service}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 relative overflow-hidden" style={{ backgroundImage: 'url(https://images.pexels.com/photos/5669602/pexels-photo-5669602.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}>
        <div className="absolute inset-0 bg-[#2C3E50]/90"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="text-center mb-16">
            <div className="text-sm uppercase tracking-[0.2em] font-medium text-[#CA9A67] mb-4">Our Approach</div>
            <h2 className="text-3xl lg:text-4xl tracking-tight text-white font-bold mb-4">Our Process</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mobile-swiper">
            {process.map((step, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 p-6 hover:bg-white/10 transition-all duration-300">
                <div className="text-4xl font-bold text-[#CA9A67] mb-4">{index + 1}</div>
                <h3 className="text-xl text-white font-semibold mb-3">{step.title}</h3>
                <p className="text-sm text-white/70 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <img src="https://images.unsplash.com/photo-1698047681432-006d2449c631?w=800&q=80" alt="Why choose our trademark team" className="w-full h-[500px] object-cover shadow-lg" />
            </div>
            <div>
              <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-8">Why Choose Us</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mobile-swiper">
                {whyChoose.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="bg-white border border-[#2C3E50]/5 shadow-sm p-6">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-[#CA9A67] mb-4">
                        <Icon className="w-6 h-6 text-white" strokeWidth={1.5} />
                      </div>
                      <h3 className="text-lg text-[#2C3E50] font-semibold mb-2">{item.title}</h3>
                      <p className="text-sm text-[#2E2E2E]/70 leading-relaxed">{item.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      <ServiceFAQ faqs={faqs} />

      <section className="py-24 md:py-32 bg-[#2C3E50]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl lg:text-4xl tracking-tight text-white font-bold mb-6">Ready to Protect Your Brand?</h2>
          <p className="text-xl text-white/90 leading-relaxed mb-8">Contact us today for professional trademark registration services.</p>
          <button onClick={onGetStarted} className="bg-[#CA9A67] text-white hover:bg-[#b8894f] transition-colors px-8 py-4 font-medium">Get Legal Help Now</button>
        </div>
      </section>
    </div>
  );
};

export default Trademark;
